"use client";

import React, { useState } from "react";
import { ArrowLeft, Calendar, Clock, Share2, Check, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";

declare const wvcClient: {
  getCurrentWPQueryParams: () => Record<string, any>;
};

function calculateReadTime(content?: string, excerpt?: string): string {
  const text = content || excerpt || "";
  const cleanText = text.replace(/<[^>]*>/g, " ").trim();
  const words = cleanText ? cleanText.split(/\s+/).filter(Boolean).length : 0;
  if (words === 0) {
    return "5 min read";
  }
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
}

function SinglePostContent() {
  const { posts, loading, error, refetch } = usePosts();
  const [copied, setCopied] = useState(false);

  const handleShare = () => {
    if (typeof window !== "undefined") {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 animate-pulse">
        <div className="h-6 w-32 rounded-md bg-muted" />
        <div className="space-y-4">
          <div className="h-10 w-3/4 rounded-md bg-muted" />
          <div className="h-5 w-1/3 rounded-md bg-muted" />
        </div>
        <div className="aspect-video w-full rounded-2xl bg-muted" />
        <div className="space-y-4 pt-4">
          <div className="h-4 w-full rounded-md bg-muted" />
          <div className="h-4 w-full rounded-md bg-muted" />
          <div className="h-4 w-5/6 rounded-md bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl text-center py-16">
        <p className="text-base text-muted-foreground">Unable to load the article at this time.</p>
        <Button onClick={refetch} variant="outline" className="mt-4">
          Try again
        </Button>
      </div>
    );
  }

  const post = posts?.[0];

  if (!post) {
    return (
      <div className="mx-auto max-w-2xl text-center py-16">
        <h2 className="text-2xl font-bold font-default text-foreground">Post not found</h2>
        <p className="mt-2 text-muted-foreground">The article you are looking for does not exist or has been moved.</p>
        <Button asChild className="mt-6">
          <Link to="/BlogPage">Return to articles</Link>
        </Button>
      </div>
    );
  }

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Recent update";

  const authorName = post.author?.name || "Brandon Beamer, M.D.";
  const authorAvatar =
    post.author?.avatar?.["96"] ||
    post.author?.avatar?.["48"] ||
    post.author?.avatar?.["24"] ||
    "";

  return (
    <article className="mx-auto max-w-4xl">
      {/* Top Navigation & Breadcrumbs */}
      <div className="mb-8 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Button variant="ghost" size="sm" asChild className="gap-2 text-muted-foreground hover:text-foreground">
            <Link to="/BlogPage">
              <ArrowLeft className="size-4" />
              Back to articles
            </Link>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleShare}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            {copied ? (
              <>
                <Check className="size-4 text-primary" />
                <span>Link copied</span>
              </>
            ) : (
              <>
                <Share2 className="size-4" />
                <span>Share article</span>
              </>
            )}
          </Button>
        </div>
        <Breadcrumbs />
      </div>

      {/* Categories */}
      {post.categories && post.categories.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {post.categories.map((cat, i) => (
            <span
              key={cat.id || i}
              data-index={i}
              className="inline-flex items-center rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {/* Prominent Title */}
      <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl font-default">
        {post.title}
      </h1>

      {/* Article Metadata */}
      <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <User className="size-4 text-primary" />
          <span className="font-medium text-foreground">{authorName}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="size-4" />
          <span>{formattedDate}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="size-4" />
          <span>{calculateReadTime(post.content, post.excerpt)}</span>
        </div>
      </div>

      {/* Featured Image */}
      {post.featuredImage && (
        <div className="mt-8 overflow-hidden rounded-2xl border border-border bg-muted shadow-sm">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            width={1200}
            height={675}
            className="aspect-video w-full object-cover"
          />
        </div>
      )}

      {/* Main Content */}
      <div className="mt-10">
        {post.content ? (
          <div
            className="prose max-w-none text-foreground leading-relaxed [&>p]:mb-6 [&>p]:text-base [&>p]:text-muted-foreground [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-foreground [&>h3]:mt-8 [&>h3]:mb-3 [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:text-foreground [&>ul]:mb-6 [&>ul]:list-disc [&>ul]:pl-6 [&>ul>li]:mb-2 [&>ul>li]:text-muted-foreground [&>ol]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol>li]:mb-2 [&>ol>li]:text-muted-foreground [&>blockquote]:my-6 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-foreground [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary/80 [&_strong]:text-foreground [&_strong]:font-semibold"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        ) : post.excerpt ? (
          <div
            className="prose max-w-none text-foreground leading-relaxed [&>p]:mb-6 [&>p]:text-base [&>p]:text-muted-foreground [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary/80 [&_strong]:text-foreground [&_strong]:font-semibold"
            dangerouslySetInnerHTML={{ __html: post.excerpt }}
          />
        ) : (
          <p className="text-base text-muted-foreground leading-relaxed">
            Content for this article will be updated shortly.
          </p>
        )}
      </div>

      <Separator className="my-12" />

      {/* Related Care Card */}
      {(() => {
        const textToMatch = `${post.title || ""} ${(post.categories || []).map((c) => c.name).join(" ")}`.toLowerCase();
        let targetRoute = "/SpecialtiesAndProceduresPage";
        let targetLabel = "Explore Specialties";
        let targetDescription = "Explore Dr. Beamer's subspecialty orthopedic sports medicine care and advanced procedures in Bozeman.";

        if (textToMatch.includes("hip")) {
          targetRoute = "/HipArthroscopyPage";
          targetLabel = "Hip Arthroscopy";
          targetDescription = "Learn about hip arthroscopy, labral repair, and FAI impingement correction in Bozeman.";
        } else if (textToMatch.includes("shoulder") || textToMatch.includes("rotator cuff")) {
          targetRoute = "/ShoulderSurgeryPage";
          targetLabel = "Shoulder Surgery";
          targetDescription = "Learn about arthroscopic rotator cuff repair, instability stabilization, and joint replacement in Bozeman.";
        } else if (textToMatch.includes("cartilage")) {
          targetRoute = "/CartilageMeniscusPage";
          targetLabel = "Cartilage & Meniscus";
          targetDescription = "Learn about cartilage restoration, osteotomies, and meniscus transplantation in Montana.";
        } else if (
          textToMatch.includes("knee") ||
          textToMatch.includes("acl") ||
          textToMatch.includes("bear") ||
          textToMatch.includes("meniscus")
        ) {
          targetRoute = "/KneeAclSurgeryPage";
          targetLabel = "Knee & ACL Surgery";
          targetDescription = "Learn about ACL reconstruction, BEAR restoration, and knee ligament care in Bozeman and Big Sky.";
        }

        return (
          <Card className="my-10 border border-border bg-secondary/60 shadow-xs">
            <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Specialized Treatment
                </span>
                <h3 className="text-xl font-bold text-foreground font-default">
                  Related care in Bozeman, Montana
                </h3>
                <p className="text-sm text-muted-foreground max-w-xl">
                  {targetDescription}
                </p>
              </div>
              <div className="flex flex-col gap-2 shrink-0 sm:max-w-xs">
                <div className="flex flex-wrap items-center gap-3">
                  <Button asChild variant="outline">
                    <Link to={targetRoute}>
                      {targetLabel}
                    </Link>
                  </Button>
                  <Button asChild>
                    <Link to="/ContactPage">
                      Request an appointment
                    </Link>
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  You can also visit Bridger Orthopedic Urgent Care in Bozeman and request Dr. Beamer for your follow-up consultation after your initial work up.
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Author Section */}
      <Card className="border border-border bg-card shadow-sm">
        <CardContent className="flex flex-col gap-6 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <Avatar className="size-16 shrink-0 border border-border">
              {authorAvatar ? (
                <AvatarImage src={authorAvatar} alt={authorName} />
              ) : null}
              <AvatarFallback className="bg-accent text-accent-foreground text-lg font-semibold">
                BB
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                Written by
              </span>
              <h3 className="text-lg font-bold text-card-foreground font-default">
                {authorName}
              </h3>
              <p className="text-sm text-muted-foreground">
                Orthopedic Sports Medicine Surgeon with medical school at Cornell, residency at Harvard, and a sports medicine fellowship at Stanford, specializing in advanced knee, hip, and shoulder preservation in Bozeman, Montana.
              </p>
            </div>
          </div>
          <div className="shrink-0">
            <Button asChild variant="outline" size="sm">
              <Link to="/AboutDrBeamerPage">About Dr. Beamer</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

export default function Article() {
  const currentWPQueryParams =
    typeof wvcClient !== "undefined" ? wvcClient.getCurrentWPQueryParams() : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-post-template-section"
      data-nav="light"
      className="bg-background text-foreground py-16 sm:py-24"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>
      </div>
    </section>
  );
}